import React from 'react';
import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import { darken, lighten, styled } from '@mui/material/styles';
const StyledButton = styled(MuiButton)(({ theme }) => ({
  borderRadius: '8px',
  fontWeight: 600,
  '&.MuiButton-sizeSmall': {
    fontSize: '14px',
    fontWeight: 600,
    height: '36px',
    padding: '0 12px',
    lineHeight: '1.375rem',
    letterSpacing: '.0288rem',
  },
  '&.MuiButton-sizeMedium': {
    fontSize: '14px',
    fontWeight: 600,
    height: '40px',
    padding: '0 14px',
    lineHeight: '1.5rem',
    letterSpacing: '.025rem',
  },
  '&.MuiButton-sizeLarge': {
    fontSize: '16px',
    fontWeight: 600,
    height: '44px',
    padding: '0 16px',
    lineHeight: '1.625rem',
    letterSpacing: '.0288rem',
  },
  '&.Mui-focusVisible': {
    outline: 'none',
    border: 'none',
    boxShadow: `0px 0px 0px 2px ${lighten(theme.palette.primary.main, 0.1)}, 
                0px 0px 0px 6px ${lighten(theme.palette.primary.main, 0.7)} !important`,
  },
  '&.Mui-disabled, &[aria-disabled="true"]': {
    color: '#98A2B3 !important',
    '& svg path': {
      stroke: '#98A2B3 !important',
    },
  },
  '&[aria-disabled="true"]': {
    pointerEvents: 'none',
  },
  '&.MuiButton-outlinedPrimary.Mui-disabled, &.MuiButton-outlinedPrimary[aria-disabled="true"]': {
    border: '1px solid #EAECF0 !important',
  },
  '&.MuiButton-containedPrimary': {
    boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    '&:hover': {
      backgroundColor: darken(theme.palette.primary.main, 0.1),
    },
  },
  '&.MuiButton-containedPrimary.Mui-disabled, &.MuiButton-containedPrimary[aria-disabled="true"]': {
    backgroundColor: '#F2F4F7',
    color: '#98A2B3',
  },
  '&.MuiButton-outlinedError.Mui-disabled, &.MuiButton-outlinedError[aria-disabled="true"]': {
    border: '1px solid #EAECF0 !important',
  },
  '&.MuiButton-containedSecondary': {
    boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    backgroundColor: '#fff',
    color: '#182230',
    border: '1px solid #D0D5DD',
    '&:hover': {
      backgroundColor: '#F9FAFB',
      borderColor: '#D0D5DD',
    },
  },
  '&.MuiButton-containedSecondary.Mui-focusVisible': {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  '&.MuiButton-outlinedSecondary': {
    boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    backgroundColor: '#fff',
    color: '#344054',
    border: '1px solid #D0D5DD',
    '&:hover': {
      backgroundColor: '#F9FAFB',
      borderColor: '#D0D5DD',
    },
  },
  '&.MuiButton-outlinedSecondary.Mui-focusVisible': {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  '&.MuiButton-outlinedPrimary': {
    border: `1px solid ${lighten(theme.palette.primary.main, 0.5)}`,
    '&:hover': {
      backgroundColor: lighten(theme.palette.primary.main, 0.9),
      color: darken(theme.palette.primary.main, 0.2),
    },
  },
  '&.MuiButton-textSecondary': {
    border: '1px solid transparent !important',
    color: '#475467',
    '&:hover': {
      backgroundColor: '#F9FAFB',
    },
  },
  '&.MuiButton-textPrimary.Mui-disabled, &.MuiButton-textPrimary[aria-disabled="true"]': {
    border: '1px solid transparent !important',
  },
  '&.MuiButton-colorError.Mui-focusVisible': {
    boxShadow: `0px 0px 0px 2px ${lighten('#D92D20', 0.1)}, 
                0px 0px 0px 6px ${lighten('#D92D20', 0.7)} !important`,
  },
  '&.MuiButton-textError': {
    border: '1px solid transparent !important',
  },
}));
/** Renders a button. Consult https://mui.com/material-ui/api/button/ for more usage info. */
function ButtonBase<C extends React.ElementType>(
  props: MuiButtonProps<
    C,
    {
      component?: C;
    }
  >,
  ref: React.Ref<HTMLButtonElement>,
): JSX.Element {
  return (
    <StyledButton {...props} ref={ref}>
      {props.children}
    </StyledButton>
  );
}
export const Button = React.forwardRef(ButtonBase);
export type ButtonProps = MuiButtonProps;
